/**
 * Shared cell / row / section TS types for the Master Schedule v3 grids.
 *
 * These mirror the camelCase contract of the live coverage-summary and
 * people-rotation endpoints; the Coverage + People grids consume them directly.
 */

import type { TFunction } from "i18next";

/**
 * Structured, localizable recurrence descriptor for the rule chip — mirrors the backend
 * `RotationRule` wire shape. The backend sends structure (never English text); the FE renders
 * the label via `formatRotationRule` so every language reads correctly.
 */
export enum RotationRuleType {
	/** Fixed weekday set, repeating every `intervalWeeks` weeks (1=weekly, 2=biweekly…). */
	Weekdays = "weekdays",
	/** Every `intervalDays` days. */
	Daily = "daily",
	/** `on` days on / `off` days off. */
	Consecutive = "consecutive",
	/** Several patterns that don't fold into one. */
	Multiple = "multiple",
	/** No recurring template — the nurse only has materialized `nurse_in_shift` assignments, so the
	 * roster shows them from their actual worked days rather than a projected cycle. */
	AssignmentsOnly = "assignmentsOnly",
	/** No pattern (outside-config / empty). */
	None = "none",
}

export type RotationRule = {
	type: RotationRuleType;
	/** Sun-first weekday indices 0..6 (Weekdays type). */
	weekdays: number[];
	intervalWeeks: number;
	intervalDays: number;
	on: number | null;
	off: number | null;
};

/** Sun-first short-weekday translation keys, indexed by the `weekdays` values (0=Sun). */
const WEEKDAY_SHORT_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

/** Render a structured `RotationRule` as its localized chip label (equivalent to the old strings). */
export const formatRotationRule = (rule: RotationRule, t: TFunction): string => {
	switch (rule.type) {
		case RotationRuleType.None:
			return "";
		case RotationRuleType.AssignmentsOnly:
			return t("schedule.template.v3.rule.assignmentsOnly");
		case RotationRuleType.Multiple:
			return t("schedule.template.v3.rule.multiple");
		case RotationRuleType.Consecutive:
			return t("schedule.template.v3.rule.consecutive", { on: rule.on ?? 0, off: rule.off ?? 0 });
		case RotationRuleType.Daily:
			return rule.intervalDays > 1 ? t("schedule.template.v3.rule.everyNDays", { n: rule.intervalDays }) : t("schedule.template.v3.rule.daily");
		case RotationRuleType.Weekdays: {
			const prefix =
				rule.intervalWeeks === 2
					? t("schedule.template.v3.rule.biweeklyPrefix")
					: rule.intervalWeeks > 2
						? t("schedule.template.v3.rule.everyNWeeksPrefix", { n: rule.intervalWeeks })
						: "";
			const body =
				rule.weekdays.length === 7
					? t("schedule.template.v3.rule.daily")
					: rule.weekdays.map((d) => t(`schedule.template.v3.rule.day.${WEEKDAY_SHORT_KEYS[d]}`)).join("/");
			return prefix + body;
		}
		default:
			return "";
	}
};

export type AssignedName = {
	/** Stable id for this assignment (drag identity in the names grid). */
	id: string;
	/**
	 * The nurse this assignment belongs to. Stable across every cell the person
	 * appears in — drives the Detail hover-highlight ("show everywhere this nurse
	 * works"). Backend-supplied (`nurseId` on the live response).
	 */
	nurseId: number;
	name: string;
	/** Short role code shown next to the name in the popover. */
	role: string;
	/** This nurse is on PTO the cell's day — still counts toward `filled` (holds the recurring slot); rendered struck-through. */
	onPto?: boolean;
	/** Charge nurse for this slot/day — carries a charge marker on the chip. */
	charge?: boolean;
	/**
	 * Overtime signal for this assignment. `"approaching"` = near the weekly
	 * threshold, `"over"` = already past it. Backend-computed (hours/OT are
	 * backend-only); the live coverage endpoint sends `"over"` when the nurse's
	 * recurring template tips into overtime this cell's week (binary, matching the
	 * People view). `"approaching"` is reserved for a future near-threshold band.
	 */
	ot?: "approaching" | "over";
};

/**
 * How a row's `target` is computed — census (ratio ladder on the typical census), max (configured
 * peak; labeled "Target" for no-ladder depts), or required (configured minimum floor; target ==
 * minimum). Ladder depts toggle census/max; no-ladder depts toggle max/required — a mismatched
 * request downgrades server-side so `targetMode` always matches a rendered toggle option.
 */
export enum CoverageTargetMode {
	Census = "census",
	Max = "max",
	Required = "required",
}

export type CoverageDayCell = {
	/** Bodies filling the slot that day — a PTO body counts (it still holds the recurring slot). */
	filled: number;
	/** Target / ideal staffing. */
	target: number;
	/** Minimum safety floor. */
	minimum: number;
	/** Whether the slot is required that day (off day / inactive week → false). */
	required: boolean;
	/** Every assigned name whose shift PTO removed this day, however short the time off — the signal a
	 * dated reader subtracts from `filled` to get who is really coming in. NOT what the grid marks. */
	ptoCount: number;
	/** The extended-leave subset of `ptoCount` — the only PTO the grid marks, matching the Weekly and
	 * Consecutive grids (a routine day or two off leaves the body in its slot unmarked). Both are
	 * backend-computed (calculations are backend-only). */
	longPtoCount: number;
	/** Names assigned to this slot+day (`onPto` flags the long-PTO ones, matching `longPtoCount`). */
	names: AssignedName[];
};

export type CoverageRow = {
	id: string;
	/** The `DepartmentLocation` record this row is (one row = one config record) — resolve the editable
	 * location by this id. Null only for outside-config rows (a filled shift with no matching record). */
	locationId?: string | null;
	location: string;
	/** Shift-time label, e.g. "7a–7p". */
	shift: string;
	/** The row's eligible short-role codes — a multi-role config row is ONE row that any-matches all of these. Render the label via `coverageRoleLabel`. */
	roles: string[];
	/** Job id this row is keyed on — populated ONLY in job-managed depts (null otherwise). In job depts, rows are keyed by job, so match/identity go by this, not `roles`. */
	jobId?: string | null;
	/** Job display name (e.g. "Nurse", "Aide") — populated ONLY in job-managed depts (null otherwise). The identity column renders this in job depts instead of a role-derived label. */
	jobName?: string | null;
	/** Structured recurrence descriptor (surfaces non-weekly patterns); render via `formatRotationRule`. */
	rule: RotationRule;
	/** True when the shift-time is outside the department config. */
	outsideConfig?: boolean;
	/** True for a configured template with no roles assigned — surfaced as a flagged, editable row, not dropped. */
	unconfiguredRoles?: boolean;
	/** `weeks × 7` day cells, row-major (Sunday-first). */
	days: CoverageDayCell[];
};

/** Display label for a coverage row's eligible-role set, e.g. "LPN/RN" (single code for outside-config rows). */
export const coverageRoleLabel = (roles: string[]): string => roles.join("/");

/** Sentinel `jobId` for a job-managed template with no job assigned (a config error) — surfaced as a
 * visible "Unassigned" row. Locale-neutral: the BE emits only this id (no English), the FE localizes. */
export const UNASSIGNED_JOB_ID = "unassigned";

/** A coverage row's identity label, resolved in ONE place so every render site agrees: the localized
 * "Unassigned" for the sentinel job, else the job name (job depts), else the role-set label (role depts). */
export const coverageRowLabel = (row: Pick<CoverageRow, "jobId" | "jobName" | "roles" | "unconfiguredRoles">, t: TFunction): string => {
	if (row.unconfiguredRoles) return t("schedule.template.v3.unconfiguredRoles");
	return row.jobId === UNASSIGNED_JOB_ID ? t("schedule.template.v3.unassignedJob") : (row.jobName ?? coverageRoleLabel(row.roles));
};

export type CoverageSection = {
	id: string;
	name: string;
	rows: CoverageRow[];
};

/** Top-level coverage-summary response (camelCase from the backend). */
export type CoverageSummaryResponse = {
	/** Scheduling horizon in weeks (`days` per row === weeks × 7). */
	weeks: number;
	/** ISO date (yyyy-MM-dd) of week 1 day 1 — the cycle anchor. */
	cycleStart: string;
	sections: CoverageSection[];
	/** Mode actually applied (no-ladder census→max; ladder required→census-or-max). */
	targetMode: CoverageTargetMode;
	/** Whether the dept has a ratio ladder — picks the census/max vs Target/Required toggle pair. */
	hasRatioLadder: boolean;
};

// ───────────────────────────────────────────────────────────────────────────
// People-axis (rotation) live contract. Hours are nested under `nurse.hours`,
// backend-computed — the FE reads them verbatim and never re-derives.
// ───────────────────────────────────────────────────────────────────────────

/**
 * A nurse's dominant worked-day shift type. Part of the live wire contract, but NOT rendered by the
 * People grid: it is one value per NURSE, so a mixed rotation would paint every day cell the same
 * wrong hue. The day cell names the job it works instead.
 */
export enum NurseShiftType {
	Day = "day",
	Night = "night",
}

/** A nurse's training status — mirrors the backend `NurseTrainingStatus`. `Regular` is the default
    "no tag" case; every other value surfaces as a tag on the People identity cell's hover tooltip. */
export enum NurseTrainingStatus {
	Trainee = "Trainee",
	Trainer = "Trainer",
	Regular = "Regular",
	Orientation = "Orientation",
	Education = "Education",
}

/** How the worked-pattern repeats — fixed weekdays vs a rolling N-day cycle. */
export enum RotationKind {
	/** Same weekdays every week (Mon/Wed/Fri, Mon–Fri, daily). */
	FixedDays = "fixedDays",
	/** A rolling cycle decoupled from the calendar week (4-on/2-off, Pitman). */
	Rotating = "rotating",
}

/** One day of a nurse's calendar projection (weeks × 7 of these per nurse). */
export type NurseRotationDay = {
	/** This nurse works this calendar day per the rotation. */
	worked: boolean;
	/** Normally-worked day they are off for PTO. `null` (falsy) when not a PTO exception. */
	pto: boolean | null;
	/**
	 * Every distinct unit this nurse works that calendar day, EARLIEST shift first (backend-supplied;
	 * empty when off, and when the day's shifts carry no unit at all). A list because a same-day
	 * double can be SPLIT across two units. Surfaced under the day marker in People · Detail as one
	 * joined line, and listed in the same order in the cell's tooltip. Read verbatim.
	 */
	locations: string[];
	/**
	 * Job ids the nurse works that calendar day, EARLIEST shift first (backend-supplied; empty
	 * when off). The day cell renders the first job's initials and lists the rest in its tooltip.
	 * Distinct from `NurseRotation.jobIds`, which is the nurse's whole enabled-job set (the filter).
	 */
	jobIds: string[];
	/**
	 * The day's am/pm shift spans (e.g. "7am-3pm"), EARLIEST first (backend-supplied; empty when off).
	 * Rendered under the pill in People · Detail. Distinct spans only — a split double reads as two,
	 * a repeated identical span as one. Formatted server-side by the SAME helper the Coverage axis
	 * uses for its shift column, so a shift's hours read identically on both axes.
	 */
	shiftLabels: string[];
	/**
	 * Free-text note(s) on the generated shift(s) this day (backend-supplied; `null` when off or when
	 * no shift carries a note). A same-day double's distinct notes arrive joined, earliest first.
	 * Surfaced in the day cell's hover tooltip.
	 */
	notes: string | null;
	/** Special training status on the earliest such shift that day; Regular is represented as null. */
	trainingStatus: NurseTrainingStatus | null;
};

/** Backend-computed hours roll-up for one nurse (FE never re-derives these). */
export type NurseRotationHours = {
	/** Worked shifts across the whole horizon. */
	totalShifts: number;
	/** Worked shifts in one rotation cycle. */
	shiftsPerCycle: number;
	/** Hours across one full rotation cycle (the honest headline). */
	cycleHours: number;
	/** Hours per calendar week across the horizon. */
	weekHours: number[];
	/** Weeks (0-based) whose hours exceed the OT threshold. */
	otWeeks: number[];
	/** Any week tips into OT. */
	hasOt: boolean;
};

/**
 * The longer projection behind a row's NORMALIZED view. Flipping a row swaps its calendar strip for a
 * multi-week block so the rotation's cycle can be read as it actually drifts across weeks, instead of
 * being cut off at the department's horizon. Backend-projected — never tile `cyclePattern` to fake it:
 * for `RotationRuleType.Multiple` nurses that pattern IS the horizon projection, so repeating it would
 * invent a rotation, and PTO/job/hours/location past the horizon only exist here.
 */
export type NurseNormalizedRotation = {
	/** `normalizedWeeks × 7` calendar days from `cycleStart` — a superset of `NurseRotation.days`. */
	days: NurseRotationDay[];
	/** OT week indices across the whole normalized span (`hours.otWeeks` covers only the visible horizon). */
	otWeeks: number[];
};

/** One nurse row from the live people-rotation endpoint. */
export type NurseRotation = {
	id: string;
	name: string;
	/** Short role code, e.g. "RN", "CNA". */
	role: string;
	/** The nurse's enabled job ids (intersected with the department's jobs) — drives the People
	    Job filter so jobs that share a role (Wound Care vs Nurse) stay distinct. */
	jobIds: string[];
	location: string;
	/** "day" | "night" (FE `NurseShiftType`) — the nurse's DOMINANT shift type; see that enum for why
	    the grid no longer renders it. */
	shiftType: string;
	/** The nurse's training status (FE `NurseTrainingStatus`) — surfaced as a tag on the identity
	    cell's hover tooltip. `Regular` shows no tag. */
	trainingStatus: NurseTrainingStatus;
	/** "fixedDays" | "rotating" (FE `RotationKind`). */
	kind: string;
	/** Structured recurrence descriptor; render via `formatRotationRule`. */
	rule: RotationRule;
	/** Length of one rotation cycle in days. */
	cycleLength: number;
	/** One cycle of worked/off booleans (length === cycleLength), the rotation seed. */
	cyclePattern: boolean[];
	/** Hours per worked shift. */
	shiftHours: number;
	/** Anchor date (cycle day 0) — shown in the cycle-strip tooltip. */
	anchorISO: string;
	/** `weeks × 7` calendar projection of the cycle, with PTO exceptions applied. */
	days: NurseRotationDay[];
	/** Backend-computed hours roll-up — read verbatim. */
	hours: NurseRotationHours;
	/** The expanded projection this row shows when normalized. */
	normalized: NurseNormalizedRotation;
};

/** Top-level people-rotation response (camelCase from the backend). */
export type PeopleRotationResponse = {
	/** Scheduling horizon in weeks (`days` per nurse === weeks × 7). */
	weeks: number;
	/** ISO date (yyyy-MM-dd) of week 1 day 1 — the cycle anchor. */
	cycleStart: string;
	/** Week count of every nurse's `normalized.days` — the width of the expanded block. Backend-sent
	    (never hardcoded here, so the two can't drift) and never below `weeks`. */
	normalizedWeeks: number;
	nurses: NurseRotation[];
};

/**
 * Section roll-up: cycle fill % (filled/target over required days) + gap count
 * (cells below minimum). `fillPct` is `null` when the section has NO configured
 * targets (no required demand at all) — there is nothing to be "filled", so the
 * header must NOT claim 100%; callers render a no-targets affordance instead.
 */
export const computeSectionRollup = (section: CoverageSection): { fillPct: number | null; gapCount: number } => {
	let filledSum = 0;
	let targetSum = 0;
	let gapCount = 0;
	for (const row of section.rows) {
		for (const d of row.days) {
			if (!d.required) continue;
			filledSum += Math.min(d.filled, d.target);
			targetSum += d.target;
			if (d.filled < d.minimum) gapCount += 1;
		}
	}
	const fillPct = targetSum === 0 ? null : Math.round((filledSum / targetSum) * 100);
	return { fillPct, gapCount };
};
